import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { api } from '../lib/api'

interface ShareLink {
  id: string
  device_id: string
  token: string
  url: string
  label: string
  expires_at: string
  created_at: string
}

interface PublicTrackData {
  device_name: string
  vehicle_type?: string
  latitude?: number
  longitude?: number
  speed?: number
  heading?: number
  last_seen_at?: string
  expires_at: string
  logo_url?: string
  brand_color?: string
  dest_latitude?: number
  dest_longitude?: number
  dest_label?: string
  eta_minutes?: number
}

export function useShareLinks() {
  return useQuery({
    queryKey: ['share-links'],
    queryFn: () => api.get<ShareLink[]>('/shares').then((r) => r.data),
  })
}

export function useCreateShareLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ deviceId, label, expiresHours, dest_latitude, dest_longitude, dest_label }: { deviceId: string; label?: string; expiresHours?: number; dest_latitude?: number; dest_longitude?: number; dest_label?: string }) =>
      api.post(`/devices/${deviceId}/share`, { label, expires_hours: expiresHours ?? 24, dest_latitude, dest_longitude, dest_label }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['share-links'] }),
  })
}

export function useDeleteShareLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/shares/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['share-links'] }),
  })
}

// Public API — no auth required. Uses raw axios (no Bearer token).
const publicApi = axios.create({ baseURL: '/api/v1', headers: { 'Content-Type': 'application/json' } })

export function usePublicTrack(token: string) {
  return useQuery({
    queryKey: ['public-track', token],
    queryFn: () => publicApi.get<PublicTrackData>(`/public/track/${token}`).then((r) => r.data),
    enabled: !!token,
    refetchInterval: 5000,
  })
}
